// ----------------------------------------------------------------------------
// Dependencies
// ----------------------------------------------------------------------------
import React, { useMemo } from 'react';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import styled from 'styled-components';
import Loading from 'components/Loading';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import COLOR from 'utils/constants/color';

// ----------------------------------------------------------------------------
// Styles & Helpers
// ----------------------------------------------------------------------------
const StyledTable = styled(Table)`
  width: 100%;
  background-color: #fff;
`;

const ViewDemo = styled.span`
  text-align: left;
  color: ${({ color }) => color};
`;

const extractTextInParentheses = (displayName) => {
  const m = displayName.match(/\(([^)]+)\)/);
  return m ? m[1] : '-';
};

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------
export default function UserCourseTable({
  data = [],
  selectedRows = [],
  handleCheckboxClick,
  handleSelectAllClick = () => {},
  rowsPerPage,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  userloading,
  userTeacher = [],
  schools = []
}) {
  // slice out only the rows for the current page
  const currentPageRows = useMemo(
    () => data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [data, page, rowsPerPage]
  );

  // precompute selection booleans
  const currentPageIds = useMemo(() => currentPageRows.map((r) => r.id), [currentPageRows]);
  const allSelected = useMemo(
    () => currentPageIds.every((id) => selectedRows.includes(id)),
    [currentPageIds, selectedRows]
  );
  const someSelected = useMemo(
    () => currentPageIds.some((id) => selectedRows.includes(id)),
    [currentPageIds, selectedRows]
  );

  return (
    <StyledTable stickyHeader aria-label="courses table">
      <TableHead>
        <TableRow>
          {[
            { label: 'School Name', align: 'left' },
            { label: 'Name', align: 'left' },
            { label: 'Code', align: 'left' },
            { label: 'Primary Teacher', align: 'left' },
            {
              label: '',
              align: 'center',
              render: () => (
                <Checkbox
                  indeterminate={someSelected && !allSelected}
                  checked={allSelected}
                  onChange={handleSelectAllClick}
                />
              )
            }
          ].map((col, idx) => (
            <TableCell
              key={idx}
              align={col.align}
              sx={{ fontSize: 16, fontWeight: 600, paddingLeft: idx === 4 ? 0 : undefined }}
            >
              {col.label || col.render()}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {isEmpty(data)
          ? userloading
            ? <TableRow><TableCell colSpan={5} align="center"><Loading size={30} /></TableCell></TableRow>
            : <TableRow><TableCell colSpan={5} align="center">No results</TableCell></TableRow>
          : currentPageRows.map((row) => {
              const teacher = find(userTeacher, (u) => u.id === row.teacherId);
              const school  = find(schools,     (s) => s.id === row.orgId);
              const isSel   = selectedRows.includes(row.id);

              return (
                <TableRow key={row.id} hover>
                  <TableCell align="left">
                    {get(school, 'displayName', '-')}
                  </TableCell>
                  <TableCell align="left">
                    {extractTextInParentheses(get(row, 'displayName', '-'))}
                    {row.disabled && (
                      <ViewDemo color={COLOR.red}>&nbsp;(Deactivated)</ViewDemo>
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {get(row, 'code', '-') || '-'}
                  </TableCell>
                  <TableCell align="left">
                    {`${get(teacher, 'firstName', '-') || '-'} ${get(teacher, 'lastName', '')}`}
                  </TableCell>
                  <TableCell align="center" padding="checkbox">
                    <Checkbox
                      checked={isSel}
                      onChange={(e) => handleCheckboxClick(e, row.id)}
                    />
                  </TableCell>
                </TableRow>
              );
            })
        }
      </TableBody>

      <TableFooter>
        <TableRow>
          {!isEmpty(data) && (
            <TablePagination
              component="div"
              count={data.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              // style the pagination buttons
              backIconButtonProps={{ color: 'secondary' }}
              nextIconButtonProps={{ color: 'secondary' }}
            />
          )}
        </TableRow>
      </TableFooter>
    </StyledTable>
  );
}